import { Button, CommonModalProps, Container, TextBox } from "@animech/ui";
import {
	FunctionComponent,
	HTMLAttributes,
	KeyboardEvent,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import * as Styled from "./open-design-modal.style";
import { OpenIcon, useUiData } from "src/ui";
import { Term } from "@definitions";
import { ApiDataContext } from "src/api";
import { ModalBase } from "src/ui/components/modals/modal-base";
import { isMobile } from "src/utility";

type OpenDesignModalProps = CommonModalProps<HTMLAttributes<HTMLDivElement>>;

type OpenDesignModalTermKeys =
	| "title"
	| "text"
	| "label"
	| "placeholder"
	| "confirm"
	| "cancel"
	| "failedRequest";
const terms: { [key in OpenDesignModalTermKeys]: Term } = {
	title: "openDesignModal:title",
	text: "openDesignModal:text",
	label: "openDesignModal:label",
	placeholder: "openDesignModal:placeholder",
	confirm: "openDesignModal:confirm",
	cancel: "openDesignModal:cancel",
	failedRequest: "openDesignModal:failedRequest",
};

type Template = {
	id: string;
	imageSrc: string;
	name: string;
	description: string;
	designId: number;
};

export const OpenDesignModal: FunctionComponent<OpenDesignModalProps> = (
	baseProps,
) => {
	const { onShouldClose } = baseProps;
	const { localizer } = useUiData();
	const { openDesign } = useContext(ApiDataContext);
	const [designId, setDesignId] = useState<string>();
	const [failedRequestText, setFailedRequestText] = useState<string>();
	const [isLoading, setIsLoading] = useState<boolean>();

	const onOpenDesign = useCallback(
		async (id: string) => {
			setIsLoading(true);
			const success = await openDesign(id);
			setIsLoading(false);

			if (success) {
				onShouldClose?.();
			} else {
				setFailedRequestText(localizer.localizeTerm(terms.failedRequest));
			}
		},
		[localizer, onShouldClose, openDesign],
	);

	const onOpen = useCallback(async () => {
		if (!designId || isLoading) {
			return;
		}
		onOpenDesign(designId);
	}, [designId, isLoading, onOpenDesign]);

	const onTextInputKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter") {
				onOpen();
			}
		},
		[onOpen],
	);

	const onTextInputChange = useCallback(
		(text: string) => {
			setDesignId(text);
			if (failedRequestText) {
				setFailedRequestText(undefined);
			}
		},
		[failedRequestText],
	);

	const templates: Template[] = useMemo(() => [], []);

	const content = useMemo(
		() => (
			<>
				<Container>
					<Styled.Text>{localizer.localizeTerm(terms.text)}</Styled.Text>

					<Styled.FieldWithButton>
						<TextBox
							appearance="ghost"
							color="neutral"
							value={designId}
							onValueChange={onTextInputChange}
							placeholder={localizer.localizeTerm(terms.placeholder)}
							required
							type="text"
							instantValueChange
							inputAttributes={{ onKeyDown: onTextInputKeyDown }}
						/>
						{isMobile() && (
							<Button onClick={onOpen} disabled={!designId} busy={isLoading}>
								{localizer.localizeTerm(terms.confirm)}
							</Button>
						)}
					</Styled.FieldWithButton>
					{failedRequestText && (
						<Styled.RequestFailedText>
							{failedRequestText}
						</Styled.RequestFailedText>
					)}
				</Container>
				{isMobile() && (
					<Styled.Templates>
						{templates.map((template) => (
							<Styled.Template
								key={template.id}
								tabIndex={0}
								onClick={() => onOpenDesign(`${template.designId}`)}
								onKeyDown={() => onOpenDesign(`${template.designId}`)}
								role="button"
							>
								<img src={template.imageSrc} />
								<Styled.TemplateName>{template.name}</Styled.TemplateName>
								<Styled.TemplateDescription>
									{template.description}
								</Styled.TemplateDescription>
							</Styled.Template>
						))}
					</Styled.Templates>
				)}
			</>
		),
		[
			designId,
			failedRequestText,
			isLoading,
			localizer,
			onOpen,
			onOpenDesign,
			onTextInputChange,
			onTextInputKeyDown,
			templates,
		],
	);

	const footerButtons = useMemo(() => {
		return isMobile()
			? null
			: [
					<Button
						key="open-button"
						onClick={onOpen}
						disabled={!designId}
						busy={isLoading}
					>
						{localizer.localizeTerm(terms.confirm)}
					</Button>,
				];
	}, [designId, isLoading, localizer, onOpen]);
	return (
		<ModalBase
			icon={<OpenIcon />}
			title={localizer.localizeTerm(terms.title)}
			footerButtons={footerButtons}
			{...baseProps}
		>
			<Styled.OpenDesignModalContent
				title={localizer.localizeTerm(terms.title)}
			>
				{content}
			</Styled.OpenDesignModalContent>
		</ModalBase>
	);
};
